import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, ChevronRight, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface OnboardingGuideProps {
  listingId: string;
  listing: any;
  onDismiss: () => void;
}

export const OnboardingGuide = ({ listingId, listing, onDismiss }: OnboardingGuideProps) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  
  const isPremium = listing.is_premium;

  // Calculate completion status for each step - different for free vs premium
  const freeSteps = [
    {
      title: "Completa tu información básica",
      description: "Añade descripción, dirección y contacto",
      completed: !!(
        listing.description && 
        listing.address && 
        listing.phone && 
        listing.email
      ),
      action: () => navigate(`/listing/${listingId}/edit`),
      actionLabel: "Editar Perfil"
    },
    {
      title: "Añade imágenes",
      description: "Sube al menos 1 foto de tu negocio",
      completed: !!(listing.images && listing.images.length >= 1),
      action: () => navigate(`/listing/${listingId}/edit`),
      actionLabel: "Subir Imágenes"
    },
    {
      title: "Considera mejorar tu plan",
      description: "Desbloquea más funciones con Premium",
      completed: false,
      action: () => navigate("/premium-upgrade"),
      actionLabel: "Ver Planes Premium"
    }
  ];

  const premiumSteps = [
    {
      title: "Completa tu información básica",
      description: "Añade descripción detallada, dirección y horarios",
      completed: !!(
        listing.description && 
        listing.address && 
        listing.phone && 
        listing.email &&
        listing.opening_hours
      ),
      action: () => navigate(`/listing/${listingId}/edit`),
      actionLabel: "Editar Perfil"
    },
    {
      title: "Galería de imágenes completa",
      description: "Sube al menos 5 fotos profesionales de tu negocio",
      completed: !!(listing.images && listing.images.length >= 5),
      action: () => navigate(`/listing/${listingId}/edit`),
      actionLabel: "Subir Imágenes"
    },
    {
      title: "Añade videos",
      description: "Sube videos de tu negocio para mayor engagement",
      completed: !!(listing.video_urls && listing.video_urls.length > 0),
      action: () => navigate(`/listing/${listingId}/edit`),
      actionLabel: "Subir Videos"
    },
    {
      title: "Configura tus ofertas",
      description: "Crea cupones y promociones para atraer clientes",
      completed: false,
      action: () => navigate(`/manage-coupons?listing=${listingId}`),
      actionLabel: "Crear Cupón"
    },
    {
      title: "Optimiza tu presencia",
      description: "Completa redes sociales y descripción SEO",
      completed: !!(
        listing.social_media && 
        listing.seo_description &&
        listing.website
      ),
      action: () => navigate(`/listing/${listingId}/edit`),
      actionLabel: "Optimizar SEO"
    }
  ];

  const steps = isPremium ? premiumSteps : freeSteps;

  const completedSteps = steps.filter(s => s.completed).length;
  const allCompleted = completedSteps === steps.length;

  useEffect(() => {
    // Auto-advance to next incomplete step
    const nextIncomplete = steps.findIndex(s => !s.completed);
    if (nextIncomplete !== -1) {
      setCurrentStep(nextIncomplete);
    }
  }, []);

  if (allCompleted) {
    return (
      <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-secondary/5">
        <CardContent className="pt-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-8 h-8 text-primary" />
              <div>
                <h3 className="font-bold text-lg">¡Perfil Completado! 🎉</h3>
                <p className="text-sm text-muted-foreground">
                  Tu negocio está optimizado para atraer más clientes
                </p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onDismiss}>
              <X className="w-4 h-4" />
            </Button>
          </div>
          <Button 
            onClick={() => navigate("/premium-upgrade")}
            className="w-full bg-gradient-primary"
          >
            Actualizar a Premium
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-primary/20">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-bold text-lg mb-1">Completa tu Perfil</h3>
            <p className="text-sm text-muted-foreground">
              {completedSteps} de {steps.length} pasos completados
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onDismiss}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-muted rounded-full h-2 mb-6">
          <div 
            className="bg-gradient-primary h-2 rounded-full transition-all duration-500"
            style={{ width: `${(completedSteps / steps.length) * 100}%` }}
          />
        </div>

        {/* Steps */}
        <div className="space-y-3">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`p-4 rounded-lg border transition-all ${
                currentStep === index
                  ? "border-primary bg-primary/5"
                  : step.completed
                  ? "border-muted bg-muted/30"
                  : "border-muted"
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  {step.completed ? (
                    <CheckCircle2 className="w-5 h-5 text-primary" />
                  ) : (
                    <Circle className="w-5 h-5 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-sm mb-1">{step.title}</h4>
                  <p className="text-xs text-muted-foreground mb-3">
                    {step.description}
                  </p>
                  {!step.completed && currentStep === index && (
                    <Button
                      onClick={step.action}
                      size="sm"
                      className="w-full"
                    >
                      {step.actionLabel}
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 p-3 bg-muted/50 rounded-lg text-center">
          <p className="text-xs text-muted-foreground">
            💡 <strong>Tip:</strong> Los perfiles completos reciben hasta{" "}
            <span className="text-primary font-semibold">3x más visitas</span>
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
